import React, { useMemo } from 'react';
import { keyframes } from '@emotion/react';
import { Box } from '@mui/material';
import {
  arcPath,
  firstName,
  layoutOrbit,
  nodesById,
  ORBIT_COLORS,
  orbitInputFromView,
  type NetworkNode,
  type NetworkView,
  type PlacedNode,
} from '@rhwb/types';

/** Beyond this many recruits their name labels start colliding. */
const MAX_LABELLED_CHILDREN = 8;
const FONT = '"Inter", "Roboto", "Helvetica", "Arial", sans-serif';

const popIn = keyframes`
  from { transform: scale(0.2); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
`;
const dropIn = keyframes`
  from { transform: translateY(-14px); opacity: 0; }
  to   { transform: translateY(0);     opacity: 1; }
`;
const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const initialsOf = (name?: string | null): string => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
};

interface OrbitSvgProps {
  view: NetworkView;
  width: number;
  /** Label for the centre node; "You" draws the word instead of initials. */
  centerLabel?: string;
  /** Dashed "?" above the centre when there's no referrer. */
  showReferrerSlot?: boolean;
  /** Dashed "+" on the inner ring for adding someone you brought in. */
  showAddSlot?: boolean;
  onNodeClick?: (node: NetworkNode) => void;
  onSlotClick?: () => void;
  onAddClick?: () => void;
}

/**
 * The Amazing Network "Orbit" for the web: the same layoutOrbit the mobile app
 * uses, drawn as plain SVG. Re-mount it (via `key`) to replay the entrance.
 */
const OrbitSvg: React.FC<OrbitSvgProps> = ({
  view,
  width,
  centerLabel = 'You',
  showReferrerSlot = false,
  showAddSlot = false,
  onNodeClick,
  onSlotClick,
  onAddClick,
}) => {
  const layout = useMemo(
    () => layoutOrbit(orbitInputFromView(view, { showReferrerSlot, showAddSlot }), width),
    [view, width, showReferrerSlot, showAddSlot]
  );

  const byId = useMemo(() => nodesById(view), [view]);

  const labelChildren = view.children.length <= MAX_LABELLED_CHILDREN;

  return (
    <Box
      component="svg"
      width={width}
      height={layout.height}
      viewBox={`0 0 ${width} ${layout.height}`}
      role="img"
      aria-label="Referral network"
      sx={{ display: 'block', overflow: 'visible', fontFamily: FONT }}
    >
      <g style={{ animation: `${fadeIn} 400ms ease-out` }}>
        {/* Family bubbles first, so rings, lines and nodes sit on top. */}
        {layout.bubbles.map((b, i) =>
          b.kind === 'pill' ? (
            <rect
              key={`b${i}`}
              x={b.x}
              y={b.y}
              width={b.w}
              height={b.h}
              rx={b.h / 2}
              fill={ORBIT_COLORS.familyFill}
              stroke={ORBIT_COLORS.familyStroke}
            />
          ) : (
            <path
              key={`b${i}`}
              d={arcPath(b)}
              fill="none"
              stroke={ORBIT_COLORS.familyFill}
              strokeWidth={b.thickness}
              strokeLinecap="round"
            />
          )
        )}
        {[layout.innerR, layout.outerR].map((r) => (
          <circle key={r} cx={layout.cx} cy={layout.cy} r={r} fill="none" stroke={ORBIT_COLORS.ring} strokeDasharray="4 5" />
        ))}
        {layout.edges.map((e, i) => (
          <line
            key={i}
            x1={e.x1}
            y1={e.y1}
            x2={e.x2}
            y2={e.y2}
            stroke={e.kind === 'up' ? ORBIT_COLORS.referrer : ORBIT_COLORS.child}
            strokeOpacity={e.kind === 'up' ? 0.7 : 0.55}
            strokeWidth={e.kind === 'up' ? 2 : 1.5}
            strokeDasharray={e.dashed ? '4 4' : undefined}
          />
        ))}
      </g>

      {layout.nodes.map((placed, i) => {
        const node = byId.get(placed.id);
        const onClick =
          // Someone outside RHWB opens the picker to change them, like the "?".
          placed.kind === 'slot' || placed.kind === 'outside' || placed.kind === 'self'
            ? onSlotClick
            : placed.kind === 'add'
              ? onAddClick
              : node && onNodeClick
                ? () => onNodeClick(node)
                : undefined;
        return (
          <OrbitNodeSvg
            key={placed.id}
            placed={placed}
            index={i}
            node={node}
            centerLabel={centerLabel}
            centerPicture={view.me?.picture ?? null}
            outsideName={view.outsideReferrer ?? null}
            count={placed.kind === 'center' ? view.me?.childCount : placed.kind === 'grandchild' ? undefined : node?.childCount}
            showLabel={
              placed.kind === 'referrer' ||
              placed.kind === 'outside' ||
              placed.kind === 'self' ||
              placed.kind === 'ancestor' ||
              placed.kind === 'family' ||
              (placed.kind === 'child' && labelChildren)
            }
            onClick={onClick}
          />
        );
      })}
    </Box>
  );
};

interface OrbitNodeSvgProps {
  placed: PlacedNode;
  index: number;
  node?: NetworkNode;
  centerLabel: string;
  centerPicture: string | null;
  /** The typed name behind an "outside" node. */
  outsideName: string | null;
  count?: number;
  showLabel: boolean;
  onClick?: () => void;
}

const OrbitNodeSvg: React.FC<OrbitNodeSvgProps> = ({
  placed,
  index,
  node,
  centerLabel,
  centerPicture,
  outsideName,
  count,
  showLabel,
  onClick,
}) => {
  const outside = placed.kind === 'outside';
  // "I found RHWB myself": a solid ring with a star where the "?" was.
  const self = placed.kind === 'self';
  const { kind, x, y, r, id } = placed;
  const clipId = `orbit-clip-${id}`;
  const picture = kind === 'center' ? centerPicture : node?.picture;
  const dashed = kind === 'slot' || kind === 'add';
  const dashColor = kind === 'slot' ? ORBIT_COLORS.referrer : ORBIT_COLORS.child;
  const fill = dashed || outside || self
    ? '#fff'
    : kind === 'overflow'
      ? ORBIT_COLORS.overflow
      : ORBIT_COLORS[kind as 'center' | 'referrer' | 'ancestor' | 'family' | 'child' | 'grandchild'];

  const upward = kind === 'referrer' || kind === 'ancestor' || kind === 'slot' || outside || self;
  const animation = upward
    ? `${dropIn} 450ms cubic-bezier(.2,.9,.3,1.2) 120ms both`
    : `${popIn} 420ms cubic-bezier(.2,.9,.3,1.3) ${kind === 'center' ? 0 : Math.min(600, 150 + index * 25)}ms both`;

  const a11y =
    kind === 'slot'
      ? 'Pick who brought you to RHWB'
      : kind === 'add'
        ? 'Add someone you brought to RHWB'
        : kind === 'overflow'
          ? `${placed.hiddenCount} more`
          : kind === 'center'
            ? centerLabel
            : outside
              ? `${outsideName ?? ''}, not in RHWB`
              : self
                ? 'You found RHWB yourself'
              : node?.name ?? '';

  const glyph = (() => {
    if (kind === 'slot') return '?';
    if (kind === 'add') return '+';
    if (kind === 'overflow') return `+${placed.hiddenCount}`;
    if (kind === 'grandchild') return null;
    if (kind === 'center') return centerLabel === 'You' ? 'You' : initialsOf(centerLabel);
    if (outside) return initialsOf(outsideName);
    if (self) return '★';
    return initialsOf(node?.name);
  })();
  const below = kind === 'child' || kind === 'family';
  const glyphColor = dashed
    ? dashColor
    : outside
      ? ORBIT_COLORS.outside
      : self
        ? ORBIT_COLORS.referrer
        : kind === 'overflow'
        ? ORBIT_COLORS.overflowText
        : '#fff';
  const glyphSize = dashed || self ? r * 1.1 : kind === 'center' ? r * 0.5 : kind === 'overflow' ? Math.max(8, r * 0.75) : r * 0.62;

  return (
    <g
      transform={`translate(${x} ${y})`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick();
        }
      }}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={a11y}
      style={{ cursor: onClick ? 'pointer' : 'default', outline: 'none' }}
    >
      <g style={{ animation, transformBox: 'fill-box', transformOrigin: 'center' }}>
        <title>{a11y}</title>
        {kind === 'center' && <circle r={r + 3} fill="#fff" style={{ filter: 'drop-shadow(0 3px 8px rgba(24,119,242,.35))' }} />}
        <circle
          r={r}
          fill={fill}
          stroke={
            dashed
              ? dashColor
              : outside
                ? ORBIT_COLORS.outside
                : self
                  ? ORBIT_COLORS.referrer
                  : kind === 'overflow'
                    ? ORBIT_COLORS.child
                    : 'none'
          }
          strokeWidth={dashed || outside || self ? 2 : 1}
          strokeDasharray={dashed ? '4 3' : undefined}
        />
        {picture && kind !== 'grandchild' && !dashed && kind !== 'overflow' ? (
          <>
            <clipPath id={clipId}>
              <circle r={r} />
            </clipPath>
            <image href={picture} x={-r} y={-r} width={r * 2} height={r * 2} clipPath={`url(#${clipId})`} preserveAspectRatio="xMidYMid slice" />
          </>
        ) : glyph ? (
          <text
            textAnchor="middle"
            dominantBaseline="central"
            fill={glyphColor}
            fontSize={glyphSize}
            fontWeight={700}
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {glyph}
          </text>
        ) : null}

        {count ? (
          <g transform={`translate(${r - 3} ${-r + 3})`} style={{ pointerEvents: 'none' }}>
            <circle r={9} fill={ORBIT_COLORS.child} stroke="#fff" strokeWidth={1.5} />
            <text textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize={10.5} fontWeight={800}>
              {count}
            </text>
          </g>
        ) : null}

        {showLabel && (node || outside || self) ? (
          <text
            // Ring and family names go under the circle; the chain's to the right.
            x={below ? 0 : r + 6}
            y={below ? r + 14 : 0}
            textAnchor={below ? 'middle' : 'start'}
            dominantBaseline={below ? 'auto' : 'central'}
            fill="#333"
            fontSize={12}
            fontWeight={600}
            // Clickable like the circle: people aim at the name too.
            style={{ userSelect: 'none' }}
          >
            {self ? (
              'Found it myself'
            ) : outside ? (
              <>
                {firstName(outsideName)}
                <tspan fill="#9CA3AF" fontWeight={500}>
                  {' '}· not in RHWB
                </tspan>
              </>
            ) : (
              firstName(node?.name)
            )}
          </text>
        ) : null}
      </g>
    </g>
  );
};

export default OrbitSvg;
